import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useLocation, useRouteMatch } from 'quartz-routing/react-router';
import { DateFilterType, DEFAULT_FILTER_VALUES, DEFAULT_SORT_VALUE, TicketListingView, sortFieldsToStrings } from '../constants/filters';
import { UserTicketStatusOption } from '../data/types';
import { ROUTES } from '../constants/routes';
import { jsx as _jsx } from "react/jsx-runtime";
function stringsToSortFields(strings) {
  return strings.map(s => {
    const isDesc = s.startsWith('-');
    return {
      propertyName: isDesc ? s.slice(1) : s,
      direction: isDesc ? 'DESCENDING' : 'ASCENDING'
    };
  });
}
const DEFAULT_STATE = {
  view: TicketListingView.TABLE,
  sort: DEFAULT_SORT_VALUE,
  offset: 0,
  createDateFilter: DEFAULT_FILTER_VALUES.createDateFilter,
  lastActivityFilter: DEFAULT_FILTER_VALUES.lastActivityFilter,
  statusFilter: DEFAULT_FILTER_VALUES.statusFilter,
  ticketView: DEFAULT_FILTER_VALUES.ticketView,
  searchString: '',
  conversationSearch: ''
};
function readFromUrl(search) {
  var _ref, _params$get, _ref2, _params$get2, _params$get3;
  const params = new URLSearchParams(search);
  const sortRaw = params.get('sort');
  let sort = DEFAULT_STATE.sort;
  try {
    if (sortRaw) sort = stringsToSortFields(JSON.parse(sortRaw));
  } catch (_unused) {
    // ignore malformed sort param
  }
  return {
    view: (_ref = params.get('view')) !== null && _ref !== void 0 ? _ref : DEFAULT_STATE.view,
    sort,
    offset: Number((_params$get = params.get('offset')) !== null && _params$get !== void 0 ? _params$get : 0),
    createDateFilter: Object.values(DateFilterType).includes(params.get('createdate')) ? params.get('createdate') : DEFAULT_STATE.createDateFilter,
    lastActivityFilter: Object.values(DateFilterType).includes(params.get('hs_lastactivitydate')) ? params.get('hs_lastactivitydate') : DEFAULT_STATE.lastActivityFilter,
    statusFilter: Object.values(UserTicketStatusOption).includes(params.get('statusFilter')) ? params.get('statusFilter') : DEFAULT_STATE.statusFilter,
    ticketView: (_ref2 = params.get('ticketView')) !== null && _ref2 !== void 0 ? _ref2 : DEFAULT_STATE.ticketView,
    searchString: (_params$get2 = params.get('searchString')) !== null && _params$get2 !== void 0 ? _params$get2 : '',
    conversationSearch: (_params$get3 = params.get('conversationSearch')) !== null && _params$get3 !== void 0 ? _params$get3 : ''
  };
}
function setOrDelete(params, key, value, defaultValue) {
  if (value === defaultValue || !value && !defaultValue) {
    params.delete(key);
  } else {
    params.set(key, value);
  }
}
function writeStateToParams(params, state) {
  setOrDelete(params, 'view', state.view, DEFAULT_STATE.view);
  setOrDelete(params, 'sort', JSON.stringify(sortFieldsToStrings(state.sort)), JSON.stringify(sortFieldsToStrings(DEFAULT_STATE.sort)));
  setOrDelete(params, 'offset', `${state.offset}`, '0');
  setOrDelete(params, 'createdate', state.createDateFilter, DEFAULT_STATE.createDateFilter);
  setOrDelete(params, 'hs_lastactivitydate', state.lastActivityFilter, DEFAULT_STATE.lastActivityFilter);
  setOrDelete(params, 'statusFilter', state.statusFilter, DEFAULT_STATE.statusFilter);
  setOrDelete(params, 'ticketView', state.ticketView, DEFAULT_STATE.ticketView);
  setOrDelete(params, 'searchString', state.searchString, '');
  setOrDelete(params, 'conversationSearch', state.conversationSearch, '');
}
const ListingsSortAndFiltersContext = /*#__PURE__*/createContext(null);
export function ListingsSortAndFiltersProvider({
  children
}) {
  const location = useLocation();
  const isOnListingsRoute = !!useRouteMatch({
    path: ROUTES.root,
    exact: true
  });
  const isOnTicketRoute = !!useRouteMatch({
    path: ROUTES.ticket
  });
  const [filters, setFiltersState] = useState(() => readFromUrl(location.search));
  useEffect(() => {
    if (!isOnListingsRoute && !isOnTicketRoute) return;
    const params = new URLSearchParams();
    writeStateToParams(params, filters);
    const search = params.toString();
    const url = search ? `?${search}` : window.location.pathname;
    //TODO: Consider swapping for history.replace
    window.history.replaceState(null, '', url);
  }, [location.pathname, isOnListingsRoute, isOnTicketRoute, filters]);
  const contextValue = useMemo(() => Object.assign({}, filters, {
    setFiltersState
  }), [filters, setFiltersState]);
  return /*#__PURE__*/_jsx(ListingsSortAndFiltersContext.Provider, {
    value: contextValue,
    children: children
  });
}
export function useListingsSortAndFiltersContext() {
  const context = useContext(ListingsSortAndFiltersContext);
  if (!context) {
    throw new Error('useListingsSortAndFiltersContext must be used within a ListingsSortAndFiltersProvider');
  }
  return context;
}